import { toPairs } from "lodash";
import queryString from "query-string";

import Sentry from "@similarweb/sentry-logger";

import { swSettings } from "app/common/services/swSettings";
import { ESharingState } from "app/components/sharingAssetModal/types";
import type { INotification, ISharing } from "app/components/sharingAssetModal/types";
import type { SegmentId } from "app/constants/MmxChannels";
import { TRAFFIC_AND_ENGAGEMENT_ENDPOINTS } from "app/pages/segments/analysis/constants/trafficAndEngagementEndpoints";
import { setCustomSegmentsMeta } from "app/pages/segments/config/segmentsConfigHelpers";
import type { ISegmentsGeographyData } from "app/pages/segments/geography/SegmentsGeographyContainer";
import type { ISegmentsMMXGraphData } from "app/pages/segments/mmx/mmxGraphData";
import { UserCustomCategoryService } from "app/services/category/userCustomCategoryService";
import type { DeviceType } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";
import type { IWordPredictions } from "pro-features/components/RulesQueryBuilder/src/RulesQueryBuilderTypes";
import type { ISite } from "pro-features/components/Workspace/Wizard/src/types";

import { DefaultFetchService } from "../fetchService";

const DEFAULT_WEB_SOURCE = "Desktop";
const DEFAULT_PAGE_SIZE_WORD_PREDICTIONS = 100000;
const DEFAULT_PAGE_SIZE = 30000;
const SHARED_SEGMENTS_END_POINT = "/api/userdata/segments/share";

export const SEGMENT_KEY_SEPARATOR = "#";
export enum SEGMENT_TYPES {
    SEGMENT = "SEGMENT",
    WEBSITE = "WEBSITE",
}

export enum ESegmentShareState {
    PUBLIC = "Public",
    PRIVATE = "Private",
    RESTRICTED = "Restricted",
}

export enum ESegmentShareStateToServer {
    RESTRICTED = 1,
    PRIVATE = 2,
    PUBLIC = 3,
}

type IMappingSharingState = {
    [key in ESharingState]: ESegmentShareState;
};

interface IWordPredictionsParams {
    from: Date | string;
    to: Date | string;
    includeSubDomains: boolean;
    isWindow: boolean;
    keys: string;
    orderBy: string;
    timeGranularity: string;
}

export interface ICustomSegmentsGroupAnalysisParams {
    segmentGroupId: string;
    country: string;
    from: Date | string;
    to: Date | string;
    webSource?: "Desktop";
    timeGranularity?: "Monthly";
    includeSubDomains?: boolean;
    isWindow?: boolean;
    keys?: string;
    lastUpdated?: string;
}

interface IWordPredictionRuleItem {
    words: string[];
    type: number;
}

export interface ISegmentBase {
    id: SegmentId;
    segmentName: string;
    domain: string;
    favicon?: string;
}

export interface ICustomSegment extends ISegmentBase {
    segmentType: number;
    rules: IWordPredictionRuleItem[];
    creationDate?: string;
    creationType?: string;
    userId?: string;
    lastUpdated?: string;
    isBlacklisted?: boolean;
    sharedWithAccounts: number[];
    sharedWithUsers: number[];
    targetType: ESegmentShareState;
    isDisabled?: boolean;
}

export type ICustomSegments = Array<ICustomSegment>;

export interface ICustomSegmentAccount extends ICustomSegment {
    userId: string;
    userName?: string;
    isLocked?: boolean;
    type?: string;
}

export interface ICustomSegmentGroupWebsite {
    id?: SegmentId;
    type?: string;
    domain: string;
    favicon: string;
}

export interface ICustomSegmentsGroup {
    id?: string;
    name: string;
    segments: string[];
    websites?: ICustomSegmentGroupWebsite[];
    isReadOnly?: boolean;
    creationTime?: string;
    lastUpdatedTime?: string;
    members?: string[];
    isDisabled: boolean;
    customIndustryId?: string;
}

type WidgetAvailabilityItem = {
    id: string;
    isEnabled: boolean;
};

export type WidgetAvailability = {
    [key: string]: WidgetAvailabilityItem;
};

interface ICustomSegmentsGroupPayload {
    id?: string;
    name?: string;
    segments?: string[];
    websites?: string[];
}

export const EMPTY_CUSTOM_SEGMENTS: ICustomSegmentsMetaData = {
    Segments: [],
    AccountSegments: [],
    SegmentGroups: [],
};

export const EMPTY_SEGMENT_MODULE: ISegmentsModule = {
    segmentsLoading: false,
    customSegmentsMeta: EMPTY_CUSTOM_SEGMENTS,
};

export interface ICustomSegmentsMetaData {
    Segments: ICustomSegment[];
    SegmentGroups: ICustomSegmentsGroup[];
    AccountSegments: ICustomSegmentAccount[];
    //happens after xhr response is returned
    FilteredAccountSegments?: ICustomSegmentAccount[];
}

type LimitationStatus = "WithinTheLimit" | "HardLimitExceed" | "SoftLimitExceed";

export type UniqueSegments = {
    [id: string]: number;
};

export interface DashboardSegmentsMeta {
    Segments: ICustomSegment[];
    SegmentGroups: ICustomSegmentsGroup[];
    LimitationStatus: LimitationStatus;
    UniqueSegments: UniqueSegments;
}

export interface ISegmentsModule {
    customSegmentsMeta?: ICustomSegmentsMetaData;
    segmentsLoading: boolean;
}

interface IApiParams {
    segmentGroupId: string;
    country: number;
    from: string;
    to: string;
    webSource?: DeviceType;
    timeGranularity: string;
    fileName: string;
    isWindow: boolean;
    includeSubDomains: boolean;
    lastUpdated?: string;
}

export interface IBaseSingleRequestParams {
    country: string;
    from: string;
    includeSubDomains: boolean;
    isWindow: boolean;
    segmentsIds: string[];
    timeGranularity: string;
    to: string;
    webSource: DeviceType;
    keys: string;
    compareFrom?: string;
    compareTo?: string;
    lastUpdated: string;
}
export interface ISegmentGeoRequestParams {
    from: string;
    to: string;
    timeGranularity: string;
    includeSubDomains: boolean;
    isWindow: boolean;
    sid: string;
    webSource: string;
    lastUpdated: string;
    country: number;
}

export interface ISegmentGeoExcelRequestParams extends ISegmentGeoRequestParams {
    fileName: string;
}

interface IUpdateSharingState {
    segment: ICustomSegment;
    currentSharingState: ESharingState;
    sharedWithAccounts: number[];
    sharedWithUsers: number[];
    notification: INotification;
}

export interface IBulkShareSegmentsParams {
    segmentIds: string[];
    newUsersAdd: number[];
    newUsersRemove: number[];
    userIds: number[];
    accountIds: number[];
    viewSharingType: number;
    message?: string;
}

export interface IBulkShareSegmentsResponse {
    userId: number[];
    newUsersIds: number[];
    accountId: number[];
    viewSharingType: number;
    assets: Array<{ assetId: string; assetName: string }>;
}

export interface IBulkUnshareSegmentsParams {
    SegmentIds: string[];
}

export interface ISegmentsApiService {
    getUserCustomSegmentsAndSegmentGroups: () => Promise<ICustomSegmentsMetaData>;
    getCustomSegmentEngagementData: (params: IBaseSingleRequestParams) => Promise<unknown>;
    createCustomSegment: (customSegment: ICustomSegment) => Promise<any>;
    updateCustomSegment: (customSegment: ICustomSegment) => Promise<any>;
    deleteCustomSegment: (segmentId: string) => Promise<any>;
    deleteCustomSegments: (segments: ICustomSegments) => Promise<boolean>;
    createCustomSegmentsGroup: (customSegmentGroup: ICustomSegmentsGroupPayload) => Promise<any>;
    updateCustomSegmentsGroup: (customSegmentGroup: ICustomSegmentsGroupPayload) => Promise<any>;
    deleteCustomSegmentsGroup: (customSegmentGroup: ICustomSegmentsGroupPayload) => Promise<any>;
    getSegmentRules: (segmentId: string) => Promise<ICustomSegment>;
    resolveSharingStateToShareModalFormat: (customSegment: ICustomSegment) => ESharingState;
    resolveSharingStateToSegmentFormat: (sharingState: ESharingState) => ESegmentShareState;
    shareSegment: (segment: ICustomSegment, sharing: ISharing) => Promise<void>;
    shareSegmentsBatch: (params: IBulkShareSegmentsParams) => Promise<IBulkShareSegmentsResponse>;
    unshareSegmentsBatch: (params: IBulkUnshareSegmentsParams) => Promise<void>;
    refreshSegmentsMetaData: () => Promise<void>;
}

/** AI (custom LOB) segments use ids with this prefix; they are omitted from normalized metadata. */
const isAISegmentId = (segmentId: string) => segmentId.startsWith("$CLB");

const filterAiSegmentIdsFromGroup = (group: ICustomSegmentsGroup): ICustomSegmentsGroup => ({
    ...group,
    segments: (group.segments ?? []).filter((segmentId) => !isAISegmentId(segmentId)),
});

const transformSegmentGroup = (group: ICustomSegmentsGroup) => {
    const websites = (group.websites ?? []).map((website) => ({
        ...website,
        id: SEGMENT_TYPES.WEBSITE + SEGMENT_KEY_SEPARATOR + website.domain,
        type: SEGMENT_TYPES.WEBSITE,
    }));
    return {
        ...group,
        websites,
        members: [...group.segments, ...websites.map((segWebsite) => segWebsite.id)],
    };
};

export default class SegmentsApiService implements ISegmentsApiService {
    private static _instance: SegmentsApiService | undefined;
    protected fetchService: DefaultFetchService;

    constructor() {
        this.fetchService = DefaultFetchService.getInstance();
    }

    public static getInstance(): SegmentsApiService {
        if (!SegmentsApiService._instance) {
            SegmentsApiService._instance = new SegmentsApiService();
        }
        return SegmentsApiService._instance;
    }

    private mappingSharingState: IMappingSharingState = {
        [ESharingState.PRIVATE]: ESegmentShareState.PRIVATE,
        [ESharingState.ACCOUNT]: ESegmentShareState.PUBLIC,
        [ESharingState.SUBSET_OF_USERS]: ESegmentShareState.RESTRICTED,
    };

    public resolveSharingStateToShareModalFormat = (record: ICustomSegment): ESharingState => {
        const { targetType } = record;
        const modalSharingState = (Object.keys(this.mappingSharingState) as unknown as ESharingState[]).find(
            (key) => this.mappingSharingState[key] === targetType,
        );
        return Number(modalSharingState) as ESharingState;
    };

    public resolveSharingStateToSegmentFormat = (shareType: ESharingState): ESegmentShareState => {
        return this.mappingSharingState[shareType];
    };

    private resolveSegmentData = (segment: ICustomSegment) => {
        const { sharedWithUsers: sharedWithUsersFromServer, sharedWithAccounts: sharedWithAccountsFromServer } =
            segment;

        return {
            ...segment,
            sharedWithUsers: sharedWithUsersFromServer ? sharedWithUsersFromServer.map(Number) : [],
            sharedWithAccounts: sharedWithAccountsFromServer ? sharedWithAccountsFromServer.map(Number) : [],
        };
    };
    private resolveAllSegmentsArrays = (allSegments: FixmeType): ICustomSegmentsMetaData => {
        const AccountSegments = allSegments.AccountSegments.filter(
            (segment: ICustomSegment) => !isAISegmentId(segment.id),
        ).map((segment: ICustomSegment) => this.resolveSegmentData(segment));
        const Segments = allSegments.Segments.filter((segment: ICustomSegment) => !isAISegmentId(segment.id)).map(
            (segment: ICustomSegment) => this.resolveSegmentData(segment),
        );
        return {
            Segments,
            AccountSegments,
            SegmentGroups: allSegments.SegmentGroups.map(filterAiSegmentIdsFromGroup).map(transformSegmentGroup),
        };
    };

    public getUserCustomSegmentsAndSegmentGroups(timeout = undefined): Promise<ICustomSegmentsMetaData> {
        // No .catch here — let it reject and leave Sentry logging to the caller (they already log on failure).
        return this.fetchService
            .get<ICustomSegmentsMetaData>("/api/userdata/segments/customsegments/metadata", undefined, {
                timeout,
                preventAutoCancellation: true,
            })
            .then((response) => this.resolveAllSegmentsArrays(response));
    }
    public getIsSegmentAvailableForDashboard(segmentId: string, dashboardId: string): Promise<boolean> {
        // @ts-ignore FIXME: strict migration - Type 'Promise<void | boolean>' is not assignable to type 'Promise<boolean>'
        return this.fetchService
            .get<boolean>(`/api/dashboards/${dashboardId}/canAddSegment/${segmentId}`)
            .catch((e) => {
                Sentry.error("Segment module - Error to fetch metadata: canAddSegment", e);
            });
    }
    public getIsSegmentRepalceAvailableForDashboard(segmentId: string, widgetId: string): Promise<boolean> {
        // @ts-ignore FIXME: strict migration - Type 'Promise<void | boolean>' is not assignable to type 'Promise<boolean>'
        return this.fetchService
            .get<boolean>(`/api/dashboards/${widgetId}/canReplaceWidgetSegment/${segmentId}`)
            .catch((e) => {
                Sentry.error("Segment module - Error to fetch metadata: canReplaceWidgetSegment", e);
            });
    }
    public getIsSegmentGroupRepalceAvailableForDashboard(segmentGroupId: string, widgetId: string): Promise<boolean> {
        // @ts-ignore FIXME: strict migration - Type 'Promise<void | boolean>' is not assignable to type 'Promise<boolean>'
        return this.fetchService
            .get<boolean>(`/api/dashboards/${widgetId}/canReplaceWidgetSegmentsGroup/${segmentGroupId}`)
            .catch((e) => {
                Sentry.error("Segment module - Error to fetch metadata: canReplaceWidgetSegmentsGroup", e);
            });
    }
    public getIsSegmentGroupAddAvailableForDashboard(segmentGroupId: string, dashboardId: string): Promise<boolean> {
        // @ts-ignore FIXME: strict migration - Type 'Promise<void | boolean>' is not assignable to type 'Promise<boolean>'
        return this.fetchService
            .get<boolean>(`/api/dashboards/${dashboardId}/canAddSegmentsGroup/${segmentGroupId}`)
            .catch((e) => {
                Sentry.error("Segment module - Error to fetch metadata: canAddSegmentsGroup", e);
            });
    }
    public getCustomSegmentsForDashboard(dashboardId: string) {
        return this.fetchService
            .get(`/api/userdata/segments/customsegments/metadata/dashboard/${dashboardId}`)
            .catch((e) => {
                Sentry.error("Segment module - Error to fetch metadata for dashboard", e);
            });
    }
    public getDashboardEnabledWidgets(dashboardId: string) {
        return this.fetchService.get(`/api/dashboards/${dashboardId}/widgetsEnabled`);
    }
    public getCustomSegmentMetaData(params: { sid: string }): Promise<ICustomSegment> {
        return this.fetchService.get(`/api/userdata/segments/customsegments/${params.sid}`);
    }
    public getCustomSegmentMarketingMixGraphExcelUrl(params: IBaseSingleRequestParams): string {
        const queryStringParams = queryString.stringify({
            ...params,
            mode: undefined,
        });
        return `/widgetApi/SegmentsMarketingMix/SegmentTrafficSources/Excel?${queryStringParams}`;
    }

    public getCustomSegmentEngagementData(params: {
        country: string;
        from: string;
        includeSubDomains: boolean;
        isWindow: boolean;
        segmentsIds?: string[];
        segmentGroupId?: string;
        timeGranularity: string;
        to: string;
        webSource?: DeviceType;
        keys: string;
        compareFrom?: string;
        compareTo?: string;
        lastUpdated: string;
        latest?: string;
    }) {
        const { webSource = devicesTypes.DESKTOP, ...paramsWithoutWebSource } = params;
        const endpoint = TRAFFIC_AND_ENGAGEMENT_ENDPOINTS[webSource].data;
        return this.fetchService.get(endpoint, paramsWithoutWebSource);
    }

    public getUniqueUsersData(params: {
        segmentsIds?: string[];
        segmentGroupId?: string;
        country: string;
        includeSubDomains: boolean;
        isWindow: boolean;
        timeGranularity: string;
        from: Date | string;
        to: Date | string;
        webSource: string;
        keys: string;
        compareFrom?: string;
        compareTo?: string;
        lastUpdated: string;
    }) {
        return this.fetchService.get("widgetApi/SegmentUniqueUsers/SegmentsUniqueUsers/Data", params);
    }

    public getWordPredictions(
        params: IWordPredictionsParams,
        rules: IWordPredictionRuleItem[],
    ): Promise<IWordPredictions> {
        const queryString = this.prepareParams(params, DEFAULT_PAGE_SIZE_WORD_PREDICTIONS);
        return this.fetchService.post(`widgetApi/PopularSegments/PopularSegmentWords/PopularWords?${queryString}`, {
            rules,
        });
    }

    public hasExistingUrlsCompundStringNoRobots(
        params: IWordPredictionsParams,
        rules: IWordPredictionRuleItem[],
    ): Promise<any> {
        const queryString = this.prepareParams(params);
        return this.fetchService.post(`widgetApi/PopularSegments/CustomMatch?${queryString}`, {
            rules,
        });
    }

    public createCustomSegment(customSegment: ICustomSegment): Promise<any> {
        return this.fetchService.post("/api/userdata/segments/customsegments", {
            ...customSegment,
        });
    }

    public updateCustomSegment(customSegment: ICustomSegment): Promise<any> {
        return this.fetchService.put("/api/userdata/segments/customsegments", {
            ...customSegment,
        });
    }
    public deleteCustomSegment(segmentId: string): Promise<any> {
        const result = this.fetchService.delete(`/api/userdata/segments/customsegments/${segmentId}`);
        // Refresh the custom industries since they might contain the deleted segment
        result.then(UserCustomCategoryService.refreshCustomCategories);
        return result;
    }

    public async deleteCustomSegments(segments: ICustomSegments): Promise<boolean> {
        const segmentIDs = segments.map(({ id }) => id);
        const payload = { SegmentIds: segmentIDs };
        const result = await this.fetchService.delete<boolean>(
            `/api/userdata/segments/customsegments`,
            undefined,
            payload,
        );
        // Refresh the custom industries since they might contain the deleted segment
        UserCustomCategoryService.refreshCustomCategories();
        await this.refreshSegmentsMetaData();
        return result;
    }

    // Segments Group CRUD
    public createCustomSegmentsGroup(customSegmentGroup: ICustomSegmentsGroupPayload): Promise<any> {
        return this.fetchService
            .post<ICustomSegmentsGroup>(`/api/userdata/segments/customsegments/group`, {
                ...customSegmentGroup,
            })
            .then(transformSegmentGroup);
    }
    public updateCustomSegmentsGroup(customSegmentGroup: ICustomSegmentsGroupPayload): Promise<any> {
        return this.fetchService
            .put<ICustomSegmentsGroup>("/api/userdata/segments/customsegments/group", {
                ...customSegmentGroup,
            })
            .then(transformSegmentGroup);
    }
    public deleteCustomSegmentsGroup(customSegmentGroup: ICustomSegmentsGroupPayload): Promise<any> {
        const { id } = customSegmentGroup;
        return this.fetchService.delete(`/api/userdata/segments/customsegments/group/${id}`);
    }
    // End Segments Group CRUD

    // Segments Group Analysis
    public getCustomSegmentsGroupAnalysis(params: ICustomSegmentsGroupAnalysisParams): Promise<any> {
        // Do not remove "null" value for keys - server must get this param inOrder to handle request
        const { webSource = devicesTypes.DESKTOP, ...paramsWithoutWebSource } = params;
        const endpoint = TRAFFIC_AND_ENGAGEMENT_ENDPOINTS[webSource].data;
        return this.fetchService.get(
            endpoint,
            {
                ...paramsWithoutWebSource,
                keys: "null",
            },
            {
                headers: {
                    "Cache-Control": "no-cache", // force no cache
                },
            },
        );
    }

    // Segments Group Analysis Download Excel
    public getCategoryConversionExcel(params: IApiParams) {
        const { webSource = devicesTypes.DESKTOP, ...paramsWithoutDesktop } = params;
        const urlParams = toPairs(paramsWithoutDesktop)
            .map(([key, value]) => {
                if (key && value) {
                    return `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`;
                }
            })
            .join("&");
        const endpoint = TRAFFIC_AND_ENGAGEMENT_ENDPOINTS[webSource].excel;
        return `/${endpoint}?${urlParams}&keys=null`;
    }

    // websites suggestions
    public fetchWebsitesSuggestions(
        term: string,
        options: { size?: number; webSource?: string } = {},
    ): Promise<ISite[]> {
        if (!term) {
            return Promise.resolve([]);
        }

        const { size, webSource } = options;
        const queryParamsObj = { term, size, webSource };
        const queryString = Object.keys(queryParamsObj)
            .filter<keyof typeof queryParamsObj>((qKey): qKey is keyof typeof queryParamsObj => qKey in queryParamsObj)
            // @ts-ignore FIXME: queryParamsObj[qKey] - Argument of type 'string | number | undefined' is not assignable to parameter of type 'string | number | boolean'
            .map((qKey) => `${encodeURIComponent(qKey)}=${encodeURIComponent(queryParamsObj[qKey])}`)
            .join("&");
        return this.fetchService.get(`/autocomplete/websites?${queryString}`);
    }

    public getSegmentGeographyData(params: ISegmentGeoRequestParams): Promise<ISegmentsGeographyData> {
        return this.fetchService.get("api/segmentsGeography/Data", params);
    }
    public getSegmentGeographyExcelUrl(params: ISegmentGeoExcelRequestParams): string {
        const queryStringParams = queryString.stringify(params);
        return `api/segmentsGeography/Excel?${queryStringParams}`;
    }

    /** Segments Analysis Marketing Mix **/

    public getCustomSegmentMarketingMixGraphData(
        params: IBaseSingleRequestParams | ICustomSegmentsGroupAnalysisParams,
    ): Promise<ISegmentsMMXGraphData> {
        return this.fetchService.get("/widgetApi/SegmentsMarketingMix/SegmentTrafficSources/Data", {
            ...params,
            mode: undefined,
        });
    }

    // End Segments Group Analysis

    private prepareParams(params: IWordPredictionsParams, pageSize: number = DEFAULT_PAGE_SIZE): string {
        return `from=${params.from}&to=${params.to}&includeSubDomains=${params.includeSubDomains}&keys=${params.keys}&orderBy=${params.orderBy}&isWindow=${params.isWindow}&timeGranularity=${params.timeGranularity}&PageSize=${pageSize}&webSource=${DEFAULT_WEB_SOURCE}`;
    }

    getSegmentRules(segmentId: string): Promise<ICustomSegment> {
        return this.fetchService.get(`/api/userdata/segments/customsegments/${segmentId}`);
    }

    public shareSegment = async (segment: ICustomSegment, sharing: ISharing): Promise<void> => {
        const { sharingState: currentSharingState, sharedWithUsers, notification } = sharing;
        const requestedTargetType = this.resolveSharingStateToSegmentFormat(currentSharingState);
        const isNewSharingState =
            currentSharingState === ESharingState.SUBSET_OF_USERS || requestedTargetType !== segment.targetType;

        if (!isNewSharingState) {
            return;
        }

        await this.updateSharingState({
            segment,
            currentSharingState,
            sharedWithAccounts: currentSharingState === ESharingState.ACCOUNT ? [swSettings.user.accountId] : [],
            sharedWithUsers: currentSharingState === ESharingState.SUBSET_OF_USERS ? sharedWithUsers : [],
            notification,
        });
    };

    private updateSharingState = async ({
        segment,
        currentSharingState,
        sharedWithAccounts,
        sharedWithUsers,
        notification,
    }: IUpdateSharingState) => {
        const { shouldNotify, notificationContent } = notification;
        const { id: segmentId, sharedWithUsers: SharedWithMeSegments } = segment;
        const SHARE_ENDPOINT = `${SHARED_SEGMENTS_END_POINT}/${segmentId}`;

        try {
            let response;
            if (currentSharingState === ESharingState.PRIVATE) {
                response = await this.fetchService.delete(SHARE_ENDPOINT);
            } else {
                const newUsersAdd = sharedWithUsers.filter(
                    (sharedUserId) => !SharedWithMeSegments?.includes(sharedUserId),
                );
                const body = {
                    accountIds: sharedWithAccounts,
                    userIds: sharedWithUsers,
                    ...(shouldNotify &&
                        currentSharingState === ESharingState.SUBSET_OF_USERS && {
                            message: notificationContent,
                            newUsersAdd: newUsersAdd,
                        }),
                };
                response = await this.fetchService.put(SHARE_ENDPOINT, body);
            }

            await this.refreshSegmentsMetaData();
            return response;
        } catch (error) {
            Sentry.error(
                `change the sharing state to a Segment is failed segmentId:${segmentId}, sharingState:${currentSharingState}`,
                error as Error,
            );
            throw error;
        }
    };
    public async shareSegmentsBatch(params: IBulkShareSegmentsParams): Promise<IBulkShareSegmentsResponse> {
        const BATCH_SHARE_ENDPOINT = `${SHARED_SEGMENTS_END_POINT}/batch`;
        try {
            const response = await this.fetchService.put<IBulkShareSegmentsResponse>(BATCH_SHARE_ENDPOINT, params);
            await this.refreshSegmentsMetaData();
            return response;
        } catch (error) {
            Sentry.errorTracker({
                message: "Batch share segments failed",
                e: error,
                params: {
                    extra: { segmentIds: params.segmentIds },
                },
            });
            throw error;
        }
    }

    public async unshareSegmentsBatch(params: IBulkUnshareSegmentsParams): Promise<void> {
        const BATCH_SHARE_ENDPOINT = `${SHARED_SEGMENTS_END_POINT}/batch`;
        try {
            await this.fetchService.delete(BATCH_SHARE_ENDPOINT, undefined, params);
            await this.refreshSegmentsMetaData();
        } catch (error) {
            Sentry.errorTracker({
                message: "Batch unshare segments failed",
                e: error,
                params: {
                    extra: { segmentIds: params.SegmentIds },
                },
            });
            throw error;
        }
    }

    public async refreshSegmentsMetaData() {
        return new Promise<void>((resolve) => {
            // Best-effort: a failed refetch shouldn't fail the caller's flow or leave this promise unresolved.
            this.getUserCustomSegmentsAndSegmentGroups()
                .then((customSegmentsMeta) => {
                    setCustomSegmentsMeta(customSegmentsMeta);
                })
                .catch((e) => {
                    Sentry.error("Segment module - Error to fetch metadata", e);
                })
                .finally(resolve);
        });
    }
}
